import { useState, useRef } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Upload, Trash2, ImageIcon, Star } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"
import type { ArealPhoto } from "@fohet/caravan-booker-shared/types"
import { sectionClass, sectionHeaderClass } from "@/lib/styleClasses"

export const PhotoSection = ({ arealId }: { arealId: string }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [uploading, setUploading] = useState(false)

    const photosQueryKey = ["admin", "photos", arealId]

    const { data: photos = [] } = useQuery({
        queryKey: photosQueryKey,
        queryFn: () => api.get<ArealPhoto[]>(`/areals/${arealId}/photos`),
        enabled: !!arealId,
    })

    const deletePhotoMutation = useMutation({
        mutationFn: (photoId: string) => api.delete(`/areals/${arealId}/photos/${photoId}`),
        onSuccess: () => {
            toast.success(t("admin.settings.toastPhotoDeleted"))
            queryClient.invalidateQueries({ queryKey: photosQueryKey })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastPhotoFailed"))
        },
    })

    const setPrimaryMutation = useMutation({
        mutationFn: (photoId: string) =>
            api.patch<ArealPhoto>(`/areals/${arealId}/photos/${photoId}`, { isPrimary: true }),
        onSuccess: () => {
            toast.success(t("admin.settings.toastPrimarySet"))
            queryClient.invalidateQueries({ queryKey: photosQueryKey })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastPrimaryFailed"))
        },
    })

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? [])
        if (files.length === 0) return
        setUploading(true)
        let failed = 0
        for (const file of files) {
            try {
                const fd = new FormData()
                fd.append("file", file)
                await api.upload(`/areals/${arealId}/photos`, fd)
            } catch {
                failed++
            }
        }
        setUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ""
        queryClient.invalidateQueries({ queryKey: photosQueryKey })
        if (failed === 0) {
            toast.success(files.length > 1
                ? t("admin.settings.toastPhotosUploaded", { count: files.length })
                : t("admin.settings.toastPhotoUploaded"))
        } else if (failed < files.length) {
            toast.warning(t("admin.settings.toastPhotosPartial", { ok: files.length - failed, failed }))
        } else {
            toast.error(t("admin.settings.toastPhotoFailed"))
        }
    }

    return (
        <div className={sectionClass}>
            <div className="flex items-center justify-between">
                <h2 className={sectionHeaderClass}>{t("admin.settings.sectionPhotos")}</h2>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 px-4 py-2 rounded-[28px] bg-charcoal text-white font-black text-sm hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-50"
                >
                    <Upload className="size-4" />
                    {uploading ? t("admin.settings.uploading") : t("admin.settings.uploadPhoto")}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                />
            </div>
            {photos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                    <ImageIcon className="size-10 mb-3 opacity-30" />
                    <p className="font-bold text-sm">{t("admin.settings.noPhotos")}</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {photos.map((photo) => (
                        <div
                            key={photo.id}
                            className="relative group rounded-2xl overflow-hidden aspect-square bg-slate-100"
                        >
                            <img
                                src={photo.url}
                                alt={photo.altText ?? ""}
                                className="w-full h-full object-cover"
                            />
                            {photo.isPrimary && (
                                <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg bg-brand text-white text-xs font-black shadow-md">
                                    <Star className="size-3 fill-white" />
                                    {t("admin.settings.isPrimary")}
                                </div>
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                {!photo.isPrimary && (
                                    <button
                                        onClick={() => setPrimaryMutation.mutate(photo.id)}
                                        disabled={setPrimaryMutation.isPending}
                                        title={t("admin.settings.setPrimary")}
                                        className="p-2 rounded-xl bg-brand text-white hover:bg-brand-dark transition-colors disabled:opacity-50"
                                    >
                                        <Star className="size-4" />
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        if (window.confirm(t("admin.settings.confirmDeletePhoto"))) {
                                            deletePhotoMutation.mutate(photo.id)
                                        }
                                    }}
                                    className="p-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors"
                                >
                                    <Trash2 className="size-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
